import type { GetRequestStatisticsResponse } from "@nlc/shared-types";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

export function useRequestStatistics() {
  const { data: userData } = useUser();
  const projectId = userData?.currentProjectId;
  const queryClient = useQueryClient();

  const query = useQuery({
    enabled: !!userData?.user && !!projectId, // Only enabled when user is authenticated and projectId is available
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    queryFn: async () => {
      const queryParams: Record<string, number | string> = {
        filter: "all", // Statistics always show "all" - not filtered by request type
      };

      if (projectId) {
        queryParams.projectId = projectId;
      }

      return await apiClient
        .url("/requests/list/statistics")
        .query(queryParams)
        .get()
        .json<GetRequestStatisticsResponse>();
    },
    // Remove filter from query key - statistics should be cached globally per project
    queryKey: [...requestListKeys.all, "statistics", projectId],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 60 * 1000 : false,
    staleTime: 4 * 60 * 1000, // 4 minutes - statistics don't change frequently
  });

  // Event-driven statistics updates
  useEffect(() => {
    // Listen for request status changes via custom events or other hooks
    // This could be enhanced with WebSocket events or server-sent events
    const handleRequestStatusChange = () => {
      // Invalidate statistics query to trigger a refetch
      queryClient.invalidateQueries({
        queryKey: [...requestListKeys.all, "statistics", projectId],
      });
    };

    // Listen for various events that could affect statistics
    const handleRequestMutation = () => {
      // Invalidate with a small delay to ensure backend processing is complete
      setTimeout(() => {
        handleRequestStatusChange();
      }, 1000);
    };

    // Listen for focus events to ensure fresh data when user returns to tab
    const handleWindowFocus = () => {
      handleRequestStatusChange();
    };

    // Add event listeners
    window.addEventListener("focus", handleWindowFocus);

    // Custom event for request mutations (can be dispatched from other hooks)
    window.addEventListener("requestStatusChanged", handleRequestMutation);
    window.addEventListener("requestCreated", handleRequestMutation);
    window.addEventListener("requestUpdated", handleRequestMutation);
    window.addEventListener("requestCompleted", handleRequestMutation);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("requestStatusChanged", handleRequestMutation);
      window.removeEventListener("requestCreated", handleRequestMutation);
      window.removeEventListener("requestUpdated", handleRequestMutation);
      window.removeEventListener("requestCompleted", handleRequestMutation);
    };
  }, [queryClient, projectId]);

  return query;
}
